// quiet place where all the ramda functions live
import * as R from 'ramda';

import { excludeKeys } from '@framework/api/utils';
import { camelizeKeys } from '@framework/utils/camelize.utils';

const indexedReject = R.addIndex(R.reject);

/**
 * @type {function}
 */
export const objFromList = R.curry((fn, list) =>
    R.reduce(R.useWith(R.mergeDeepLeft, [R.identity, fn]), {}, list)
);

export const sortByNameCaseInsensitive = R.sortBy(R.compose(R.toLower, R.prop('name')));

export const sortByOrder = R.sortBy(R.prop('order'));

// truncate: String -> Integer -> String
export const truncate = (value, n) =>
    R.when(
        R.propSatisfies(R.gt(R.__, n), 'length'),
        R.pipe(R.take(n), R.append('…'), R.join(''))
    )(value);

export const toObjectByProp = (prop) =>
    objFromList((obj) => ({
        [obj[prop]]: obj
    }));

export const toObjectById = toObjectByProp('id');
export const toObjectByValue = toObjectByProp('value');

/**
 * Omits certain keys from an object based on a map of predications instead
 * of a single predication for the whole list of keys (see `omitWhen`).
 * @param {object} predicationMap a map of predications to iterate through the object
 * @param {object} object the object to iterate based on predication map
 * @returns {object} a stripped version of the object based on the result of predication map
 *
 * @example
 *
 * // omits `name` if it's null or empty, `age` if it's more than 20
 * mapOmitWhen({
 *   name: isNilOrEmpty,
 *   age: R.lt(20)
 * }, {
 *   name: 'Paulo',
 *   age: 25
 * });
 * // returns { name: 'Paulo' }
 */
export const mapOmitWhen = R.curry((predicationMap, obj) =>
    R.mergeRight(
        R.omit(R.keys(predicationMap), obj),
        indexedReject(
            (value, index) => predicationMap[R.keys(predicationMap)[index]](value),
            R.pick(R.keys(predicationMap), obj)
        )
    )
);

export const extractProps = R.curry((keys, list) =>
    R.reduce(
        (acc, key) => ({
            ...acc,
            [key]: R.pluck(key, list)
        }),
        {},
        keys
    )
);

const mergeBy = (prop) =>
    R.pipe(R.map(R.indexBy(R.prop(prop))), R.reduce(R.mergeWith(R.mergeRight), {}), R.values);

export const mergeById = (collection) => mergeBy('id')(collection);

// uncurryN a function based on applied args length
export const uncurry =
    (fn) =>
    (...args) =>
        R.apply(R.uncurryN(R.length(args), fn), args);

export const filterArrayByProps = R.curry((propsArr, fn, value) =>
    R.filter(R.pipe(R.props(propsArr), R.flatten, fn(value)))
);

/**
 * Maps over an object's keys applying fn.
 * @param {function} fn Transformations to the object. fn(obj)(key)
 * @param {object} obj
 * @returns {array}
 */
export const listFromObj = R.curry((fn, obj) => R.pipe(R.keys, R.map(fn(obj)))(obj));

export const propOrAttr = R.curry((object, attr) => R.propOr(attr, attr, object));

/**
 * Creates an array of unique values that are included in all given arrays
 * @param {...Array} [arrays] The arrays to inspect.
 * @returns {Array} Returns the new array of intersecting values.
 * @example
 *
 * intersect([2, 1], [2, 3]);
 * // => [2]
 */
export const intersect = (...arrays) => {
    const init = arrays.length === 0 ? [] : arrays[0];
    return arrays.reduce((acc, array) => acc.filter((item) => array.includes(item)), init);
};

export const getId = R.prop('id');

export const getValuesDifferences = (prevValues = {}, values = {}, keys = []) => {
    return keys.reduce((sum, key) => {
        if (!R.equals(values[key], prevValues[key])) {
            // eslint-disable-next-line no-param-reassign
            sum[key] = values[key];
        }
        return sum;
    }, {});
};

/**
 * @type {(a: string) => (b: { id: string }) => boolean}
 */
export const idEquals = (id) => R.propEq(id, 'id');

export const isNilOrEmpty = R.either(R.isNil, R.isEmpty);
export const isNotNilAndNotEmpty = R.complement(R.either(R.isNil, R.isEmpty));

export const groupStatesByNameId = (list) =>
    camelizeKeys(
        objFromList(
            ({ stateName, stateId, value }) => ({
                /**
                 * A fallback for user states that have empty string as the `stateId`.
                 * In rare cases, where a native client would save a user state with empty
                 * `stateId`, BE would return multiple states that have the same `stateName`
                 * but with different `stateId`. Previously only one of them will be saved to the
                 * store, depending on the order of the object in the list (first come first served)
                 * and it could be problematic when the one with non-empty `stateId` is not saved.
                 * Now they both are saved.
                 */
                [stateName]: { [stateId || 'emptyId']: value }
            }),
            R.reject(
                ({ stateName, value }) => stateName === 'meeting_document_is_unread' && value,
                list
            )
        ),
        excludeKeys
    );
